import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import {EVENTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import {ButtonSize} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import AddIcon from 'ui-library/icons/AddIcon';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {readableTime} from 'util/DateUtil';
import * as Modals from 'util/Modals';

import {ListEventSubscriptions} from './__generated/ListEventSubscriptions';
import CreateEventbridgeSubscriptionModal from './CreateEventbridgeSubscriptionModal';
import {
  DESTINATION_TYPE_TO_DISPLAY_TEXT,
  isAppSubscription,
  isEventBridgeSubscription,
} from './EventsConstants';
import * as EventsDAO from './EventsDAO';
import UpdateEventSubscriptionModal from './UpdateEventSubscriptionModal';

type EventSubscription = ListEventSubscriptions['eventSubscriptionsListing']['eventSubscriptions'][number];

export const EVENT_SUBSCRIPTIONS_LISTING_QUERY = gql`
  query ListEventSubscriptions @noReadOnly @skipAudit @engComponent(name: EVENTS) {
    eventSubscriptionsListing {
      eventSubscriptions {
        ...EventSubscriptionGraphQL
      }
      availableAwsRegions
    }
  }
  ${getFragmentWrapper('EventSubscription').fragment}
`;

export enum EventbridgeSubscriptionStatus {
  ACTIVE = 'Active',
  PENDING = 'Pending',
  EXPIRED = 'Expired',
}

interface EventSubscriptionsTableProps {
  availableAwsRegions: Array<string>;
  eventSubscriptions: Array<EventSubscription>;
  onRefreshListing: () => void;
}

export function getEventSubscriptionStatus(
  eventSubscription: EventSubscription
): EventbridgeSubscriptionStatus {
  if (isEventBridgeSubscription(eventSubscription) && eventSubscription.paused) {
    return EventbridgeSubscriptionStatus.EXPIRED;
  }
  if (isEventBridgeSubscription(eventSubscription) && eventSubscription.expiresAt != null) {
    return EventbridgeSubscriptionStatus.PENDING;
  }
  return EventbridgeSubscriptionStatus.ACTIVE;
}

export function getTooltipTextByStatus(
  state: EventbridgeSubscriptionStatus,
  subscription: EventSubscription
): string {
  if (isAppSubscription(subscription)) {
    return 'App is configured to receive events';
  }
  switch (state) {
    case EventbridgeSubscriptionStatus.ACTIVE:
      return 'Event subscription is active and has an associated AWS Event Bus.';
    case EventbridgeSubscriptionStatus.EXPIRED:
      return 'Delete or re-subscribe to this expired subscription.';
    case EventbridgeSubscriptionStatus.PENDING:
      return 'Activate subscription by associating an AWS Event Bus in the target AWS account before the expiration date.';
    default:
      throw new Error(
        `Expected state to be in [${Object.keys(EventbridgeSubscriptionStatus)}]. Actual: ${state}`
      );
  }
}

function DeliveryInformation(props: {subscription: EventSubscription; rowIndex: number}): JSX.Element {
  const {subscription, rowIndex} = props;
  if (isEventBridgeSubscription(subscription)) {
    return (
      <Cell>
        <div>
          AWS Account:
          <code
            {...getTestProps({
              component: 'EventSubscription',
              element: 'awsAccountId',
              index: rowIndex.toString(),
            })}
          >
            {subscription.awsAccountId}
          </code>
        </div>
        <div>
          AWS Region:
          <code
            {...getTestProps({
              component: 'EventSubscription',
              element: 'region',
              index: rowIndex.toString(),
            })}
          >
            {subscription.awsRegion}
          </code>
        </div>
      </Cell>
    );
  }
  if (isAppSubscription(subscription)) {
    return (
      <Cell>
        <div>App: {subscription.name}</div>
      </Cell>
    );
  }

  return <Cell />;
}

function EventSubscriptionsTable(props: EventSubscriptionsTableProps): JSX.Element {
  function handleOpenCreateModal(): void {
    analytics.deprecatedTrackEvent(EVENTS_ANALYTICS_ACTIONS.OPENED_EVENT_SUBSCRIPTION_CREATE_MODAL);
    legacyRunModal(CreateEventbridgeSubscriptionModal, {
      availableAwsRegions: props.availableAwsRegions,
      existingSubscriptions: props.eventSubscriptions,
      onRequestSubmit: () => {
        props.onRefreshListing();
      },
    });
  }

  function handleOpenUpdateModal(eventSubscription: EventSubscription, expired: boolean): void {
    analytics.deprecatedTrackEvent(EVENTS_ANALYTICS_ACTIONS.OPENED_EVENT_SUBSCRIPTION_UPDATE_MODAL);
    legacyRunModal(UpdateEventSubscriptionModal, {
      eventSubscription,
      expired,
      onRequestSubmit: () => {
        props.onRefreshListing();
      },
    });
  }

  interface EventbridgeSubscriptionStatusCellProps {
    eventSubscription: EventSubscription;
    dataTestAttrs?: DataTestAttrs;
  }

  // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
  function EventSubscriptionStatusCell(props: EventbridgeSubscriptionStatusCellProps): JSX.Element {
    const state = getEventSubscriptionStatus(props.eventSubscription);
    return (
      <Cell>
        <Tooltip
          container="body"
          placement={TooltipPlacement.TOP}
          title={getTooltipTextByStatus(state, props.eventSubscription)}
        >
          <div className="u-display-inline-block" {...getTestProps(props.dataTestAttrs)}>
            <BenchlingIcon
              className={classNames(
                'u-mini-spacing-right',
                css(
                  state === EventbridgeSubscriptionStatus.ACTIVE && styles.active,
                  state === EventbridgeSubscriptionStatus.PENDING && styles.pending,
                  state === EventbridgeSubscriptionStatus.EXPIRED && styles.expired
                )
              )}
              icon="dot"
            />
            {state}
            {isEventBridgeSubscription(props.eventSubscription) &&
              state === EventbridgeSubscriptionStatus.PENDING && (
                <div>Expires On: {readableTime(props.eventSubscription.expiresAt)}</div>
              )}
          </div>
        </Tooltip>
      </Cell>
    );
  }
  interface UpdateEventSubscriptionIconCellProps {
    eventSubscription: EventSubscription;
    dataTestAttrs?: DataTestAttrs;
  }

  // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
  function UpdateEventSubscriptionIconCell(props: UpdateEventSubscriptionIconCellProps): JSX.Element {
    // Only EventbridgeSubscription can be "EXPIRED". Therefore non-Eventbridge Subscriptions will default to "edit"
    const expired =
      getEventSubscriptionStatus(props.eventSubscription) === EventbridgeSubscriptionStatus.EXPIRED;
    return (
      <Tooltip container="body" placement={TooltipPlacement.TOP} title={expired ? 'Re-subscribe' : 'Edit'}>
        <BenchlingIcon
          className={classNames('updateIcon', css(styles.updateIcon))}
          onClick={(e) => {
            handleOpenUpdateModal(props.eventSubscription, expired);
          }}
          icon={expired ? 'refresh' : 'edit'}
          dataTestAttrs={props.dataTestAttrs}
        />
      </Tooltip>
    );
  }

  async function handleDeleteSubscription(eventSubscription: EventSubscription): Promise<void> {
    if (isAppSubscription(eventSubscription)) {
      return;
    }
    const confirmed = await Modals.confirm(
      'Delete Subscription',
      `Clicking OK will delete the subscription ${eventSubscription.name} permanently.`,
      'danger',
      {promiseCompatible: true}
    );
    if (confirmed) {
      await EventsDAO.deleteEventSubscription(eventSubscription.id);
      notifications.success('Successfully deleted subscription');
      props.onRefreshListing();
    }
  }
  return (
    <Table
      numRows={props.eventSubscriptions.length}
      emptyTableText="No event subscriptions"
      getRowClassName={() => css(styles.tableRow)}
    >
      <Column
        header={
          <Cell>
            Subscription
            <AddIcon onClick={handleOpenCreateModal} altText="Add subscription" />
            <ExportAuditLogButton
              className="u-spacing-left"
              buttonSize={ButtonSize.MINI}
              ids={props.eventSubscriptions.flatMap((sub) => sub.logIds)}
            />
          </Cell>
        }
        renderCell={(rowIndex: number) => props.eventSubscriptions[rowIndex].name}
      />
      <Column
        header="Status"
        renderCell={(rowIndex: number) => {
          const eventSubscription = props.eventSubscriptions[rowIndex];
          const status = getEventSubscriptionStatus(eventSubscription);
          return isAppSubscription(eventSubscription) || isEventBridgeSubscription(eventSubscription) ? (
            <EventSubscriptionStatusCell
              eventSubscription={eventSubscription}
              dataTestAttrs={{
                component: 'EventSubscription',
                element: 'status',
                key: status,
                index: rowIndex.toString(),
              }}
            />
          ) : (
            <Cell />
          );
        }}
      />
      <Column
        header="Delivery Method"
        renderCell={(rowIndex: number) =>
          DESTINATION_TYPE_TO_DISPLAY_TEXT[props.eventSubscriptions[rowIndex].destinationType]
        }
      />
      <Column
        header="Delivery Information"
        renderCell={(rowIndex: number) => (
          <DeliveryInformation subscription={props.eventSubscriptions[rowIndex]} rowIndex={rowIndex} />
        )}
      />
      <Column
        header="Event Types"
        renderCell={(rowIndex: number) => (
          <Cell>
            {props.eventSubscriptions[rowIndex].eventTypes.map((eventType, i) => (
              <React.Fragment key={eventType}>
                <div>
                  <code
                    {...getTestProps({
                      component: 'EventSubscription',
                      element: 'eventType',
                      index: rowIndex.toString(),
                    })}
                  >
                    {eventType}
                  </code>
                </div>
              </React.Fragment>
            ))}
          </Cell>
        )}
      />
      <Column
        renderCell={(rowIndex: number) => (
          <Cell className="u-text-align-right">
            {isEventBridgeSubscription(props.eventSubscriptions[rowIndex]) ? (
              <div className="u-display-inline-block">
                <UpdateEventSubscriptionIconCell
                  eventSubscription={props.eventSubscriptions[rowIndex]}
                  dataTestAttrs={{
                    component: 'EventSubscription',
                    element: 'updateButton',
                    index: rowIndex.toString(),
                  }}
                />
                <BenchlingIcon
                  className={classNames('deleteIcon', 'u-spacing-left', css(styles.deleteIcon))}
                  onClick={(e) => {
                    handleDeleteSubscription(props.eventSubscriptions[rowIndex]);
                  }}
                  icon="format-delete"
                />
              </div>
            ) : null}
          </Cell>
        )}
      />
    </Table>
  );
}

export default function EventSubscriptionsListing(): JSX.Element {
  const filterEventSubscriptions = (subs: Array<EventSubscription>): Array<EventSubscription> =>
    subs.filter((sub) => isEventBridgeSubscription(sub) || (isAppSubscription(sub) && !sub.paused));
  return (
    <BenchlingQuery<ListEventSubscriptions>
      query={EVENT_SUBSCRIPTIONS_LISTING_QUERY}
      renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
    >
      {(data, {refetch}) => (
        <div>
          <EventSubscriptionsTable
            availableAwsRegions={data.eventSubscriptionsListing.availableAwsRegions}
            eventSubscriptions={filterEventSubscriptions(data.eventSubscriptionsListing.eventSubscriptions)}
            onRefreshListing={refetch}
          />
        </div>
      )}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  tableRow: {
    ':hover .deleteIcon': {
      visibility: 'visible',
    },
    ':hover .updateIcon': {
      visibility: 'visible',
    },
  },
  updateIcon: {
    color: Colors.GRAY_DARK,
    visibility: 'hidden',
    cursor: 'pointer',
    ':hover': {
      color: Colors.BLUE_LIGHT,
      visibility: 'visible',
    },
  },
  deleteIcon: {
    color: Colors.GRAY_DARK,
    visibility: 'hidden',
    cursor: 'pointer',
    ':hover': {
      color: Colors.RED_LIGHT,
      visibility: 'visible',
    },
  },
  pending: {
    color: Colors.BLUE_LIGHT,
  },
  active: {
    color: Colors.GREEN,
  },
  expired: {
    color: Colors.GRAY_DARK,
  },
});
